<template>
  <div class="dictionary-term">
    <div class="header">
      <search-bar
        :placeholder="t('dictionary_search_label')"
        active
        :keyword="searchKeyword"
        @input="updateSearchInput"
        @search="handleSearch"
      />
    </div>
    <div class="content">
      <empty-placeholder v-if="showDefault">
        <template #icon>
          <SearchEmpty />
        </template>
        {{ t("dictionary_search_flag_label") }}
      </empty-placeholder>
      <div v-else-if="showResult">
        <div class="term">{{ searchResults.matched }}</div>
        <div v-if="!hasMultipleDefinitions" class="single-result">
          {{ matchedResult[0].pinyin }}
        </div>
        <div v-else class="multiple-results">
          <dropdown-component :value="pronunciations[0]" @change="handlePronunciationChange">
            <cv-dropdown-item v-for="option in pronunciations" :key="option" :value="option">
              <span>{{ option }}</span>
            </cv-dropdown-item>
          </dropdown-component>
        </div>
        <simple-media v-if="hasAudio" :key="'audio' + index" :media-data="audioData" />

        <Tabs>
          <template #trigger>
            <tab-controls-item
              v-for="(translation, index) in translations"
              :key="translation"
              :class="tabsCssVars(index)"
              @click="handleTabSelected(index)"
            >
              <h5 class="title">{{ getTabLabel(translation) }}</h5>
            </tab-controls-item>
          </template>
          <template #controls>
            <tab-controls-item
              v-for="(translation, index) in translations"
              :key="translation"
              :class="tabsCssVars(index)"
              @click="handleTabSelected(index)"
            >
              <h5 class="title">{{ getTabLabel(translation) }}</h5>
            </tab-controls-item>
          </template>

          <template #content>
            <tab-content-item
              v-for="(translation, index) in translations"
              :key="index"
              :class="tabsCssVars(index)"
            >
              <ol>
                <li v-for="(item, index) in getAllDefinitions(translation)" :key="index">
                  {{ item.definition }}
                  <ul v-if="hasExample(item.example)">
                    <li>{{ item.example }}</li>
                  </ul>
                </li>
              </ol>
            </tab-content-item>
          </template>
        </Tabs>

        <!--- Adding key here will re-render the audio/image when index(pinyin) is changed -->
        <simple-media v-if="hasImage" :key="'image' + index" :media-data="imageData" />
      </div>
      <empty-placeholder v-else>
        <template #icon>
          <SearchEmpty />
        </template>
        {{ t("dictionary_search_no_result_message") }}
      </empty-placeholder>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { isEmpty } from "@utils/var.check";
import { DICTIONARY_LANGUAGES } from "@plugins/dictionary/dictionary.constant";
import appConfigs from "@configs/app";
import SearchEmpty from "@images/placeholder/empty-states/SearchEmpty.svg?component";
import SimpleMedia from "@plugins/media/components/SimpleMedia.vue";
import * as dictionaryApi from "@apis/dictionary.api";
import { MEDIA_TYPE } from "@plugins/media/media.constant";
import { useErrorHandler } from "@/plugins/common/composables/error";

interface Props {
  term?: string;
  dictionaryLanguage?: string;
  dictionarySettings?: string;
  searchTerm?: boolean;
  context: string;
  confirmAddToNote?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  term: "",
  dictionaryLanguage: "",
  dictionarySettings: "",
  searchTerm: false,
  confirmAddToNote: false,
});

const emit = defineEmits<{
  onConfirmAddToNote: [content: unknown];
  termSearched: [
    data: { hasSearchResult: boolean; hasSearchAction: boolean; hasHitEnter: boolean },
  ];
}>();

const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();

const isLoading = ref(false);
const hasSearchAction = ref(false);
const hasHitEnter = ref(false);
const searchInput = ref("");
const searchKeyword = ref("");
const searchResults = ref<unknown>(null);
const selectedTab = ref(0);
const matchedResult = ref<unknown>(null); // there might be different pronunciation on result
const translations = ref<string[]>([]);
const index = ref(0); //index for pronunciation, default is first one

const hasSearchResult = computed(() => !isEmpty(searchResults.value));

const hasImage = computed(() => !isEmpty(matchedResult.value[index.value].media.image));

const hasAudio = computed(() => !isEmpty(matchedResult.value[index.value].media.audio));

const hasMultipleDefinitions = computed(() => matchedResult.value.length > 1);

const audioUrl = computed(() => {
  if (hasAudio.value) {
    return `${appConfigs.s3ResourceDomain}/dictionary/${props.dictionaryLanguage}${
      matchedResult.value[index.value].media.audio
    }`;
  }
  return "";
});

const imageUrl = computed(() => {
  if (hasImage.value) {
    return `${appConfigs.s3ResourceDomain}/dictionary/${props.dictionaryLanguage}${
      matchedResult.value[index.value].media.image
    }`;
  }
  return "";
});

const audioData = computed(() => ({
  mediaType: MEDIA_TYPE.AUDIO,
  mediaUrl: audioUrl.value,
}));

const imageData = computed(() => ({
  mediaType: MEDIA_TYPE.IMAGE,
  mediaUrl: imageUrl.value,
}));

const pronunciations = computed(() => {
  const pronunciationsList: unknown[] = [];
  searchResults.value.result.forEach((result) => {
    pronunciationsList.push(result.pinyin);
  });
  return pronunciationsList;
});

const isSearchContext = computed(() => props.context === "search");

const showDefault = computed(() => isSearchContext.value && !hasSearchAction.value);

const showResult = computed(() => !isLoading.value && hasSearchResult.value);

const isTabSelected = computed(() => (value: number) => value === selectedTab.value);

const tabsCssVars = computed(() => (tabIndex: number) => ({
  active: isTabSelected.value(tabIndex),
}));

const handleSearch = (event) => {
  if (hasSearchResult.value && searchResults.value.matched?.trim() === event.keyword?.trim()) {
    // do not re-trigger search if result and search input are the same
    return;
  }
  hasHitEnter.value = true;
  hasSearchAction.value = true;
  searchKeyword.value = event.keyword;
  searchDictionary(searchKeyword.value.trim());
};

const handlePronunciationChange = (pronunciation) => {
  index.value = pronunciations.value.indexOf(pronunciation);
};

const searchDictionary = (term) => {
  isLoading.value = true;
  dictionaryApi
    .search({
      term,
      translations: translations.value,
      language: props.dictionaryLanguage,
    })
    .then((response) => {
      if (response.code !== "ok") {
        passiveApiFailHandler();
        isLoading.value = false;
        return;
      }

      searchResults.value = response.data;
      matchedResult.value = response.data ? response.data.result : null;
      if (matchedResult.value) {
        processResults(matchedResult.value);
      }
      isLoading.value = false;
      emit("termSearched", {
        hasSearchResult: hasSearchResult.value,
        hasSearchAction: hasSearchAction.value,
        hasHitEnter: hasHitEnter.value,
      });
    }, passiveApiFailHandler);
};

const processResults = (results) => {
  translations.value.forEach((language) => {
    results.forEach((result) => {
      const data = result.definitions[language];
      data.map((obj) => {
        if (obj.example) {
          obj.example = getExample(obj.example);
        }
      });
    });
  });
};

const updateSearchInput = (value) => {
  searchInput.value = value.keyword;
};

const getTabLabel = (name) => {
  const codeValue = Object.values(DICTIONARY_LANGUAGES).find((code) => code.name === name);
  return t(codeValue?.displayName ?? "");
};

const getExample = (example) => {
  return `${t("dictionary_term_examples_prefix")} ${example}`;
};

const getAllDefinitions = (translation) => {
  return matchedResult.value[index.value].definitions[translation];
};

const getDefinition = () => {
  const language = translations.value[selectedTab.value];
  return matchedResult.value[index.value].definitions[language];
};

const getContentForTab = () => {
  return {
    definition: getDefinition(),
    term: searchKeyword.value,
    pronunciation: matchedResult.value[index.value].pinyin,
  };
};

const hasExample = (example) => {
  return !isEmpty(example);
};

const populateTranslations = () => {
  if (isEmpty(props.dictionarySettings) || !isEmpty(translations.value)) return;
  const translationsList = props.dictionarySettings.split(",");
  // if found chinese, to make sure chinese render on first tab
  const chineseTranslationIndex = translationsList.indexOf(DICTIONARY_LANGUAGES.ZH.name);
  if (chineseTranslationIndex > 0) {
    translationsList.splice(chineseTranslationIndex, 1);
    translationsList.unshift(DICTIONARY_LANGUAGES.ZH.name);
  }
  // if found tamil, to make sure tamil render on first tab
  const tamilTranslationIndex = translationsList.indexOf(DICTIONARY_LANGUAGES.TA.name);
  if (tamilTranslationIndex > 0) {
    translationsList.splice(tamilTranslationIndex, 1);
    translationsList.unshift(DICTIONARY_LANGUAGES.TA.name);
  }
  // if found malay, to make sure malay render on first tab
  const malayTranslationIndex = translationsList.indexOf(DICTIONARY_LANGUAGES.MS.name);
  if (malayTranslationIndex > 0) {
    translationsList.splice(malayTranslationIndex, 1);
    translationsList.unshift(DICTIONARY_LANGUAGES.MS.name);
  }
  translations.value = translationsList;
};

const handleTabSelected = (selected: number) => {
  selectedTab.value = selected;
};

const setDefaultData = () => {
  searchInput.value = props.term;
  searchKeyword.value = props.term;
  searchResults.value = null;
  hasSearchAction.value = false;
  index.value = 0;
  hasHitEnter.value = false;
};

watch(
  () => props.searchTerm,
  (value) => {
    if (value) {
      setDefaultData();
      populateTranslations();
      searchDictionary(props.term);
    }
  },
);

watch(
  () => props.confirmAddToNote,
  (value) => {
    if (value) {
      emit("onConfirmAddToNote", getContentForTab());
    }
  },
);

onBeforeMount(() => {
  populateTranslations();
});

defineExpose({
  setDefaultData,
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.dictionary-term {
  > .content {
    max-height: 15rem;
    overflow: auto;
    /*IOS Safari*/
    transform: translateZ(0);
    .empty-placeholder {
      margin: 0;
      padding: 0;
    }
  }
  .search-bar {
    margin-bottom: $spacing-05;
  }

  .term {
    @include carbon--type-style("productive-heading-03", true);
  }

  .tabs {
    ol {
      margin: $spacing-05 0;
      padding-left: $spacing-06;
      list-style-type: decimal;
    }
    ul {
      padding-left: $spacing-05;
      list-style-type: disc;
    }
  }

  .multiple-results {
    margin-top: $spacing-03;
    width: 120px;
  }
}
</style>
